import type { Component } from 'react';
import { useState } from 'react';

import BpkSelectableChip, {
  CHIP_TYPES,
} from '@skyscanner/backpack-web/bpk-component-chip';
import { withButtonAlignment } from '@skyscanner/backpack-web/bpk-component-icon';
import CalendarIcon from '@skyscanner/backpack-web/bpk-component-icon/sm/calendar';
import BpkMobileScrollContainer from '@skyscanner/backpack-web/bpk-component-mobile-scroll-container';

import { useMBDContext } from '../../common/MBD/useMBDContext';
import DealsList from '../DealsList/DealsList';

import type { DealsEvolvedProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/DealsEvolved';

import STYLE from './DealsTabs.module.scss';

const iconsMap: { [key: string]: typeof Component } = {
  calendar: withButtonAlignment(CalendarIcon),
};
const EMPTY_DEALS_TAB = { title: '0', deals: [] };

const DealsTabs = (props: DealsEvolvedProps) => {
  const {
    airline,
    countryViewUrl,
    dealsLimit,
    filteringTabDeals,
    isMobile,
    pageTypeOptions,
    priceBandStrings,
    shouldShow3by2Formation,
    strings,
    trackingContext,
  } = props;

  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const componentName = `${rawComponentName}_tab`;

  const onToggleGroup = (index: number) => {
    if (index !== activeGroupIndex && index >= 0) {
      setActiveGroupIndex(index);
    }
  };

  const tab = filteringTabDeals?.[activeGroupIndex];
  const tabDeals = tab?.deals || [];

  const getTabTitle = (index: number) =>
    (filteringTabDeals?.[index] || EMPTY_DEALS_TAB).title;
  const makeSlug = (title: string) =>
    title.toLocaleLowerCase().replaceAll(/\s+/g, '-');

  const tabId = (index: number) =>
    `deals-evolved-tab-${makeSlug(getTabTitle(index))}`;
  const panelId = (index: number) =>
    `deals-evolved-panel-${makeSlug(getTabTitle(index))}`;

  const icon = (titleIcon: string | undefined): React.ReactNode | null => {
    if (titleIcon && iconsMap[titleIcon]) {
      const Icon = iconsMap[titleIcon];
      return <Icon />;
    }
    return null;
  };

  return (
    <div className={STYLE.DealsTabs}>
      {filteringTabDeals && filteringTabDeals?.length >= 2 && (
        <BpkMobileScrollContainer className={STYLE.DealsTabs__tabsList}>
          {filteringTabDeals?.map(({ tabName, title, titleIcon }, index) => (
            <BpkSelectableChip
              onClick={() => onToggleGroup(index)}
              accessibilityLabel={title}
              selected={index === activeGroupIndex}
              aria-selected={index === activeGroupIndex ? 'true' : 'false'}
              role="tab"
              type={CHIP_TYPES.default}
              key={title}
              id={tabId(index)}
              data-index={index}
              aria-controls={panelId(index)}
              className={STYLE.DealsTabs__tab}
              leadingAccessoryView={icon(titleIcon)}
              data-tracking-element-id={`${componentName}_${index}`}
              data-tracking-common-params={buildMBDParam(componentName, {
                tab: tabName,
              })}
            >
              {title}
            </BpkSelectableChip>
          ))}
        </BpkMobileScrollContainer>
      )}

      <div
        id={panelId(activeGroupIndex)}
        role="tabpanel"
        aria-labelledby={tabId(activeGroupIndex)}
      >
        <DealsList
          {...{
            strings,
            deals: tabDeals,
            airline,
            originCode: trackingContext.originCode,
            destinationCode: trackingContext.destinationCode,
            isMobile,
            trackingContext,
            pageTypeOptions,
            priceBandStrings,
            shouldShow3by2Formation,
            countryViewUrl,
            dealsLimit,
          }}
        />
      </div>
    </div>
  );
};

export default DealsTabs;
